import { Injectable } from '@angular/core';

import { isAction } from '@frontend/sports/common/core/utils/redux';
import { bufferWithSize } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { values } from 'lodash-es';
import { concatMap, debounceTime, filter, map } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { pickIdFactory } from '../../../core/picks/pick-id';
import PickSubscriptionActions, { IPickSubscriptionPayload } from '../../picks/pick-subscription-actions';
import { betslipTypeStateSelector } from '../../types/selectors';
import { EditBetActions, IEditBetUpdatedPayload } from '../actions';

@Injectable()
export default class PushEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
    ) {}

    /**
     * When pick is swapped subscribe to the new pick and unsubscribe from the old one.
     */
    handleSwapPickActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.swapPick),
            concatMap((action) => {
                return [
                    PickSubscriptionActions.unsubscribeFromPick({ pickId: action.payload.oldPick.id }),
                    PickSubscriptionActions.subscribeToPick({
                        pick: {
                            pickId: action.payload.newPick.id,
                            context: action.payload.newPick.subscriptionContext,
                            fromHybridFixture: action.payload.newPick.fromHybridFixture,
                        },
                    }),
                ];
            }),
        );
    });

    /**
     * When fill the edit bet with picks subscribe to them. When exit edit bet unsubscribe from all picks
     */
    handleFillEditBetActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.fillEditMyBet),
            map((action) => {
                const picks: IPickSubscriptionPayload[] = values(action.payload.pickState).map((p) => ({
                    pickId: p.pick.id,
                    context: p.pick.subscriptionContext,
                    fromHybridFixture: p.pick.fromHybridFixture,
                }));

                return PickSubscriptionActions.subscribeToPicks({ picks });
            }),
        );
    });

    /**
     * Handle exit edit bet action
     */
    handleExitEditBetActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.exitEditBet),
            map(() => PickSubscriptionActions.unsubscribeAll()),
        );
    });

    /**
     * When pick is added to edit bet staging area subscribe to it.
     */
    handleAddPickToContainerActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.addPickToContainer),
            map((action) =>
                PickSubscriptionActions.subscribeToPick({
                    pick: {
                        pickId: action.pick.id,
                        context: action.pick.subscriptionContext,
                        fromHybridFixture: action.pick.fromHybridFixture,
                    },
                }),
            ),
        );
    });

    /**
     * When pick is removed from edit bet staging area unsubscribe from it.
     */
    handleRemovePickFromContainerActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.removeFromContainer),
            map((action) => {
                return PickSubscriptionActions.unsubscribeFromPick({ pickId: pickIdFactory(action.pickId) });
            }),
        );
    });

    /**
     * Handle canceling of adding pick. Unsubscribe from picks
     */
    handleCancelAddSelectionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.cancelAddSelectionMode),
            map((action) => {
                const pickIds = action.payload.map((p) => p.id);

                return PickSubscriptionActions.unsubscribeFromPicks({ pickIds });
            }),
        );
    });

    /**
     * Handle edit bet push event. Event or Fixture update, or Early payout update.
     * When such event comes debounce 500ms and wait for another push event.
     * The business logic: When odds are changed usually cause early payout value change,but we want to have only one change
     * and possible winnings to be calculated only once, otherwise the possible winnings are calculated first with the new
     * stake or odds and then again from the second event.
     */
    handleEditBetPushActionEffect$ = createEffect(() => {
        const maxUpdateQueueCount = 10;
        const updateQueueTimeoutInMilliseconds = 200;

        function createEditBetUpdatedPayload(actions: Action[]): IEditBetUpdatedPayload {
            const payload: IEditBetUpdatedPayload = {
                earlyPayout: null,
                v1FixtureMessages: [],
                v2FixtureMessages: [],
            };

            for (const action of actions) {
                if (isAction(action, BetslipActions.eventUpdate)) {
                    payload.v1FixtureMessages.push(action.message);
                }
                if (isAction(action, BetslipActions.fixtureUpdate)) {
                    payload.v2FixtureMessages.push(action.message);
                }
                if (isAction(action, EditBetActions.earlyPayoutUpdated)) {
                    payload.earlyPayout = action.payload; // we need only last one EP update value and resulted picks.
                }
            }

            return payload;
        }

        const filteredActions = this.actions$.pipe(
            ofType(EditBetActions.earlyPayoutUpdated, BetslipActions.eventUpdate, BetslipActions.fixtureUpdate),
            concatLatestFrom(() =>
                this.store.select(betslipTypeStateSelector).pipe(map((typeState) => typeState.base.currentSelectedType === BetslipType.EditBet)),
            ),
            filter(([action, isEditBet]) => isEditBet),
            map(([action, isEditBet]) => action),
        );
        const debounceTimer = filteredActions.pipe(debounceTime(updateQueueTimeoutInMilliseconds));

        return filteredActions.pipe(
            bufferWithSize(maxUpdateQueueCount, debounceTimer),
            map((actions) => EditBetActions.editBetUpdated({ payload: createEditBetUpdatedPayload(actions) })),
        );
    });
}
